import React from "react";
import { useT } from "@/lib/LanguageContext";
import { cn } from "../../../lib/utils";
import { FOCUS_RING } from "./classes";

/**
 * The CRM list table: uppercase muted headers, sortable columns ("Click any column
 * header"), a checkbox per row plus select-page, and a whole-row click that opens the
 * record (the checkbox cell stops the click). Scrolls horizontally below `md`; the
 * container carries `data-lenis-prevent` so the trackpad's horizontal swipe reaches it.
 *
 * `columns`: `[{key, header, sortKey?, render(row), className?}]`
 * `sort`: `{field, dir}`; `onSort(field)` toggles.
 * `selection`: the `useSelection` object, or omitted for a table without checkboxes.
 * `ids`: `{table, row(id), check(id), checkAll, sort(key)}` test-id builders.
 */
export default function DataTable({ columns, rows, rowKey = (r) => r.id, onRowClick, sort, onSort, selection, ids = {}, caption }) {
  const t = useT("crm");
  const pageIds = rows.map(rowKey);
  return (
    <div className="overflow-x-auto overscroll-x-contain" data-lenis-prevent>
      <table data-testid={ids.table} className="w-full min-w-[720px] text-sm text-eh-ink">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-eh-border-soft">
            {selection && (
              <th scope="col" className="w-10 py-3 pl-2 text-left">
                <input
                  type="checkbox"
                  aria-label={t("table.selectPage")}
                  checked={selection.isPageSelected(pageIds)}
                  onChange={() => selection.togglePage(pageIds)}
                  data-testid={ids.checkAll}
                  className="h-4 w-4 accent-eh-sage-deep"
                />
              </th>
            )}
            {columns.map((col) => {
              // BLOCKED-T3-3, fixed in the v1.12 integration round: `col.sortKey` and
              // `sort` are both optional (a table with no sort prop and no sortable
              // columns, e.g. PacksTable) — `undefined === undefined` was `true`, so
              // `active` fired for every column and `sort.dir` below crashed on the
              // then-undefined `sort`. Both must be present before comparing.
              const active = Boolean(sort) && Boolean(col.sortKey) && sort.field === col.sortKey;
              const ariaSort = active ? (sort.dir === "asc" ? "ascending" : "descending") : "none";
              return (
                <th key={col.key} scope="col" aria-sort={col.sortKey ? ariaSort : undefined}
                  className={cn("py-3 px-2 text-left text-xs font-semibold uppercase tracking-wider text-eh-muted", col.className)}>
                  {col.sortKey && onSort ? (
                    <button type="button" onClick={() => onSort(col.sortKey)} data-testid={ids.sort?.(col.sortKey)}
                      className={cn("inline-flex items-center gap-1 uppercase tracking-wider", FOCUS_RING)}>
                      {col.header}
                      <span aria-hidden="true" className={active ? "text-eh-ink" : "text-eh-muted"}>
                        {active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                      </span>
                    </button>
                  ) : col.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const id = rowKey(row);
            return (
              <tr
                key={id}
                data-testid={ids.row?.(id)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn("border-b border-eh-border-soft", onRowClick && "cursor-pointer hover:bg-eh-cream-deep")}
              >
                {selection && (
                  <td className="py-3 pl-2" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={t("table.selectRow")}
                      checked={selection.isSelected(id)}
                      onChange={() => selection.toggle(id)}
                      data-testid={ids.check?.(id)}
                      className="h-4 w-4 accent-eh-sage-deep"
                    />
                  </td>
                )}
                {columns.map((col) => (
                  <td key={col.key} className={cn("py-3 px-2 align-middle", col.className)}>{col.render(row)}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
